/**
 * System-managed enum definitions — single source of truth.
 *
 * System enums are created automatically at server boot via `initializeSystemEnums()`.
 * The enums themselves are immutable (name/description cannot be changed by users),
 * but the OPTIONS within each enum are fully user-configurable via CRUD operations.
 *
 * To add a new system enum:
 *   1. Add it to the appropriate *_SYSTEM_ENUMS constant below
 *   2. Register the constant in SYSTEM_ENUM_REGISTRIES at the bottom of this file
 *   That's it — the backend picks it up automatically on next deploy.
 */

// ---------------------------------------------------------------------------
// Rebates
// ---------------------------------------------------------------------------

export const REBATES_SYSTEM_ENUMS = {
  AGREEMENT_TAGS: {
    name: "Rebate Agreement Tags",
    description: "Tags for categorizing and filtering rebate agreements",
    options: {
      DUPLICATE: {
        name: "Duplicate",
        description: "Indicates this agreement was created via duplication",
      },
    },
  },
  RULE_TAGS: {
    name: "Rebate Rule Tags",
    description: "Tags for categorizing and filtering rebate rules",
  },
  SUPPLIER_TAGS: {
    name: "Supplier Tags",
    description: "Tags for categorizing and filtering suppliers",
  },
  BANNER: {
    name: "Banner",
    description: "Banners for divisions",
  },
  PRODUCT_FAMILY_TAGS: {
    name: "Product Family Tags",
    description:
      "Tags for mapping product groups to profitability data product family descriptions",
  },
} as const;

export type RebatesSystemEnumKey = keyof typeof REBATES_SYSTEM_ENUMS;

// ---------------------------------------------------------------------------
// Email Rules
// ---------------------------------------------------------------------------

export const EMAIL_RULES_SYSTEM_ENUMS = {
  EMAIL_RULE_EVENTS: {
    name: "Email Rule Events",
    description:
      "User-defined event types that can be triggered by inbound emails",
  },
} as const;

export type EmailRulesSystemEnumKey = keyof typeof EMAIL_RULES_SYSTEM_ENUMS;

// ---------------------------------------------------------------------------
// Communications
// ---------------------------------------------------------------------------

export const COMMUNICATIONS_SYSTEM_ENUMS = {
  CONTACT_TAGS: {
    name: "Contact Tags",
    description: "Tags for categorizing and filtering contacts",
    options: {
      AP_REMITTANCE: {
        name: "AP Remittance",
        description:
          "Contact receives accounts-payable remittance emails for their supplier",
      },
      SALESPERSON: {
        name: "Salesperson",
        description:
          "Contact represents a salesperson — typically resolved from the source ERP and used to route customer/invoice ownership and short-pay outreach",
      },
    },
  },
} as const;

export type CommunicationsSystemEnumKey =
  keyof typeof COMMUNICATIONS_SYSTEM_ENUMS;

// ---------------------------------------------------------------------------
// Core / Watches
// ---------------------------------------------------------------------------

export const CORE_SYSTEM_ENUMS = {
  WATCH_ROLES: {
    name: "Watch Roles",
    description:
      "Roles for notification targeting (e.g., price approver, customer success). Org-specific — no defaults.",
  },
} as const;

export type CoreSystemEnumKey = keyof typeof CORE_SYSTEM_ENUMS;

// ---------------------------------------------------------------------------
// Auto-derived registry — used by backend initializeSystemEnums()
// ---------------------------------------------------------------------------

export interface SystemEnumOptionDefinition {
  name: string;
  description: string;
}

export interface SystemEnumDefinition {
  name: string;
  description: string;
  options?: SystemEnumOptionDefinition[];
}

/**
 * All *_SYSTEM_ENUMS constants. Add new module constants here so
 * they are automatically initialized at server boot.
 */
const SYSTEM_ENUM_REGISTRIES = [
  REBATES_SYSTEM_ENUMS,
  EMAIL_RULES_SYSTEM_ENUMS,
  COMMUNICATIONS_SYSTEM_ENUMS,
  CORE_SYSTEM_ENUMS,
] as const;

/**
 * Flat list of every system enum definition (with hardcoded options),
 * derived from the registries above.
 * Imported by the backend's `initializeSystemEnums()` — no manual list to maintain.
 */
export const ALL_SYSTEM_ENUM_DEFINITIONS: SystemEnumDefinition[] =
  SYSTEM_ENUM_REGISTRIES.flatMap((registry) =>
    Object.values(registry).map((entry) => ({
      name: entry.name,
      description: entry.description,
      ...("options" in entry && entry.options
        ? {
            options: Object.values(
              entry.options as Record<
                string,
                { name: string; description: string }
              >,
            ),
          }
        : {}),
    })),
  );
